<template>
  <div class="flex flex-col justify-start gap-3">
    <div
      ref="dropContainer"
      @dragover.prevent="dragOver"
      @dragleave.prevent="dragLeave"
      @drop.prevent="handleDropFiles"
      class="w-full border border-romanSilver bg-white flex flex-col justify-center items-center gap-3 py-6 rounded-xl"
    >
      <c-icon icon-name="storage" size="m" class="text-romanSilver" />
      <button
        @click="triggerInputFile"
        class="bg-dynamicBackBtn text-white rounded-lg px-4 py-2 text-sm font-semibold"
      >
        Browse File
      </button>
      <input
        type="file"
        @change="handleInputFiles"
        ref="fileInput"
        hidden
        accept=".pdf,.doc,.docx,.xlsx,.csv,.ppt,.pptx,.png,.jpg,.jpeg"
      />
      <p class="text-romanSilver text-xs text-center px-4">
        *supported file type: pdf, doc, xlsx, csv, ppt and png or jpg. (Single file only)
      </p>
      <p class=" text-sm text-darkPurple">Drag and drop file here OR</p>
    </div>

    <div class="flex flex-col justify-start gap-3 mt-4">
      <p v-if="uploadingFiles.length > 0" class="font-semibold text-xs text-romanSilver uppercase">UPLOADING</p>
      <p v-if="uploadingFiles.length === 0" class="font-bold text-center text-jet text-sm">NO UPLOADS IN PROGRESS</p>
      <div v-for="(file, index) in uploadingFiles" :key="index" class="flex justify-between items-center gap-2 p-2 border border-romanSilver rounded-md">
        <div class="flex items-center gap-2 flex-grow">
          <div class="w-9 h-9">
            <c-icon icon-name="pdf" size="xs" class="icon" v-if="file.fileType === 'pdf'"/>
            <c-icon icon-name="doc" size="xs" class="icon" v-else-if="file.fileType === 'doc'"/>
            <c-icon icon-name="xlsx" size="xs" class="icon" v-else-if="file.fileType === 'xlsx'"/>
            <c-icon icon-name="csv" size="xs" class="icon" v-else-if="file.fileType === 'csv'"/>
            <c-icon icon-name="ppt" size="xs" class="icon" v-else-if="file.fileType === 'ppt'"/>
            <c-icon icon-name="png" size="xs" class="icon" v-else-if="file.fileType === 'png'"/>
            <c-icon icon-name="jpg" size="xs" class="icon" v-else-if="file.fileType === 'jpg' || file.fileType === 'jpeg'"/>
          </div>
          <div class="flex flex-col flex-grow">
            <span class="text-sm text-darkPurple font-normal">{{ file.name }}</span>
            <span v-if="file.status === 'interrupted'" class="text-xs text-flame">Upload Interrupted</span>
            <span v-else-if="file.status === 'uploading'" class="text-xs text-romanSilver">Uploading...</span>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <button
            v-if="file.status === 'interrupted'"
            @click="retryUpload(file, index)"
            class="p-1 hover:bg-ghostWhite rounded"
            title="Retry"
          >
            <c-icon icon-name="icon-undo" size="xs" class="text-blueCrayola" />
          </button>
          <button
            @click="cancelUpload(index)"
            class="p-1 hover:bg-ghostWhite rounded"
            title="Cancel"
          >
            <c-icon icon-name="icon-close" size="xs" class="text-romanSilver" />
          </button>
        </div>
      </div>
    </div>
    
    <div v-if="uploadedFiles.length > 0" class="flex flex-col justify-start gap-3 mt-4">
      <p class="font-semibold text-xs text-romanSilver uppercase">UPLOADED FILES</p>
      <div v-for="(file, index) in uploadedFiles" :key="index" class="flex justify-between items-center gap-2 p-2 border border-romanSilver rounded-md">
        <div class="flex items-center gap-2 flex-grow">
          <div class="w-9 h-9">
            <c-icon icon-name="pdf" size="xs" class="icon" v-if="file.mimeType === 'pdf'"/>
            <c-icon icon-name="doc" size="xs" class="icon" v-else-if="file.mimeType === 'doc'"/>
            <c-icon icon-name="xlsx" size="xs" class="icon" v-else-if="file.mimeType === 'xlsx'"/>
            <c-icon icon-name="csv" size="xs" class="icon" v-else-if="file.mimeType === 'csv'"/>
            <c-icon icon-name="ppt" size="xs" class="icon" v-else-if="file.mimeType === 'ppt'"/>
            <c-icon icon-name="png" size="xs" class="icon" v-else-if="file.mimeType === 'png'"/>
            <c-icon icon-name="jpg" size="xs" class="icon" v-else-if="file.mimeType === 'jpg' || file.mimeType === 'jpeg'"/>
          </div>
          <div class="flex flex-col flex-grow">
            <span class="text-sm text-darkPurple font-normal">{{ file.fileName }} {{ file.mimeType }}</span>
            <span class="text-xs text-romanSilver">
              <span class="text-blueCrayola">handovernote</span>
              <span class="text-romanSilver"> • {{ formatFileSize(file.fileSize) }}</span>
            </span>
          </div>
        </div>
        <button
          @click="removeUploadedFile(index)"
          class="p-1 hover:bg-ghostWhite rounded"
          title="Delete"
        >
          <c-icon icon-name="trash-icon" size="xs" class="text-flame" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import CIcon from "@/components/Icon"
import axios from "axios"

export default {
  name: "HandoverUpload",
  components: {
    CIcon,
  },
  data() {
    return {
      uploadingFiles: [],
      uploadedFiles: [],
      mimeType: {
        "vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
        "vnd.openxmlformats-officedocument.wordprocessingml.document": "doc",
        "vnd.openxmlformats-officedocument.presentationml.presentation": "ppt",
        "vnd.ms-powerpoint": "ppt",
        "pptx": "ppt"
      }
    }
  },
  methods: {
    triggerInputFile() {
      this.$refs.fileInput.click();
    },
    dragOver(e) {
      e.currentTarget.style.backgroundColor = "#f5f5f5";
    },
    dragLeave(e) {
      e.currentTarget.style.backgroundColor = "#FFFFFF";
    },
    handleDropFiles(e) {
      e.currentTarget.style.backgroundColor = "#FFFFFF";
      const files = e.dataTransfer.files;
      if (files.length > 0) {
        this.processFiles(files);
      }
    },
    handleInputFiles(event) {
      const files = event.target.files;
      if (files.length > 0) {
        this.processFiles(files);
      }
    
      event.target.value = '';
    },
    processFiles(files) {
      if (files.length > 1) {
        return this.$toasted.error("You can only upload one file for handover note.", { duration: 6000 });
      }
      
      if (this.uploadedFiles.length > 0) {
        return this.$toasted.error("Please remove the existing file before uploading a new one.", { duration: 6000 });
      }
      
      const file = files[0];
      this.validateAndUploadFile(file);
    },

    validateAndUploadFile(file) {
      if (this.uploadedFiles.length > 0) {
        return this.$toasted.error("Please remove the existing file before uploading a new one.", { duration: 6000 });
      }

      const extension = file.type.split('/').pop().toLowerCase();
      let type = this.mimeType[extension] || extension;
      const allowedFileType = ["pdf", "doc", "xlsx", "csv", "ppt", "png", "jpg", "jpeg", "sheet"];

      if (!allowedFileType.includes(type)) {
        return this.$toasted.error("Unsupported file type.", { duration: 6000 });
      }

      const fileObj = {
        name: file.name,
        size: file.size,
        file: file,
        fileType: type,
        status: 'uploading',
      };

      this.uploadingFiles.push(fileObj);
      this.uploadFile(fileObj);
    },
    async uploadFile(fileObj) {
      const file = fileObj.file;
      const payload = {
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size
      };

      try {
        const { data } = await this.$_uploadLargerFile(payload);
        await axios.put(data.uploadUrl, file, {
          headers: {
            'Content-Type': file.type,
          }
        });

        const index = this.uploadingFiles.findIndex(f => f.name === fileObj.name);
        if (index > -1) {
          this.uploadingFiles.splice(index, 1);
          
          let name = file.name;
          let fileName = name.substr(0, name.lastIndexOf(".")).replace(/[\d\(\)-]+/g, '');
          const fileExtension = file.type.split('/').pop().toLowerCase();
          let mimeType = this.mimeType[fileExtension] || fileExtension;
          
          this.uploadedFiles.push({
            fileUrl: data.fileUrl,
            fileSize: file.size,
            mimeType: mimeType,
            fileName: fileName,
          });
          this.$emit('files-updated', this.uploadedFiles);
        }

        this.$toasted.success('File uploaded successfully', { duration: 3000 });
      } catch (error) {
        const index = this.uploadingFiles.findIndex(f => f.name === fileObj.name);
        if (index > -1) {
          this.uploadingFiles[index].status = 'interrupted';
        }
        this.$toasted.error('File upload failed', { duration: 3000 });
      }
    },
    retryUpload(fileObj, index) {
      this.uploadingFiles[index].status = 'uploading';
      this.uploadFile(fileObj);
    },
    cancelUpload(index) {
      this.uploadingFiles.splice(index, 1);
    },
    removeUploadedFile(index) {
      this.uploadedFiles.splice(index, 1);
      this.$emit('files-updated', this.uploadedFiles);
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
  },
}
</script>

<style scoped>
.icon {
  width: 100% !important;
  height: 100% !important;
}
</style>
